<template>
  <div
    v-if="pricing?.tieredPrice && pricing?.tieredPrice?.length != 0"
    :class="['pricing', 'price-simplification', isLargeCard ? 'is-large-card' : '']"
  >
    <!-- Value pricing -->
    <template v-if="!isLargeCard">
      <template v-if="displaySalePriceOnly">
        <div class="price-reg display-sale-price-only">
          {{ getLabelWithPrice }}
        </div>
      </template>
      <template v-else-if="hasBadgePriceData">
        <!-- NEW Badge Pricing added -->
        <div
          :class="{ 'discount': hasBadgeDiscountOnValue }"
          class="price-reg badges-pricing-ind-tier"
          :aria-label="badgePriceData?.accessibility"
        >
          <span aria-hidden="true">{{ badgePriceData?.salePrice }}
            <span
              v-if="badgePriceData?.rangePrice"
              class="range-price"
            >- {{ badgePriceData?.rangePrice }}</span>
            <span
              :class="['sale-percent', {'percent-small' : simplificationNonBoldPercentageExp}]"
            >{{ badgePriceData?.salePercent }}</span>
          </span>
          <div
            v-if="!isLastActId && hasPromoCode"
            class="small badge-wrapper"
          >
            {{ withCodeExperiment }} {{ badgePriceData?.promo }}<button
              v-if="badgePriceData?.description && !overridePriceSimplification && !isBcom"
              v-tooltip.bottom="{
                value: getTooltipContent(badgePriceData),
                escape: true
              }"
              class="tooltip-small"
              @click.once="plpToolTipClick()"
            />
          </div>
        </div>
        <div
          v-if="valuePricing && !overridePriceSimplification"
          :class="['badge-value-pricing', {'margin-top-xxs': hasPromoCode}]"
          :aria-label="valuePricing?.accessibility"
        >
          <span class="price-strike-sm">{{ valuePricing?.price }}</span> <span class="price-sm">{{ valuePricing?.value }}</span>
        </div>
        <div
          v-else-if="!overridePriceSimplification"
          :class="['current-prev-value-labels', {'margin-top-xxs': hasPromoCode}]"
          :aria-label="currentPrevValueLabels?.accessibility"
        >
          <span
            v-if="!simplificationRemoveSaleExp && !isBcom"
            class="price-sm"
          >{{ currentPrevValueLabels?.salePrice }} {{ currentPrevValueLabels?.rangePrice }}</span>
          <span class="price-strike-sm">{{ currentPrevValueLabels?.strikePrice }} {{ currentPrevValueLabels?.rangeStrike }}</span>
        </div>
      </template>
      <template v-else-if="hasOnlyTieredPrice3">
        <div>
          <span
            :class="[valuePriceOnSale?.saleType != 'regular' ? 'discount' : 'price-reg', 'is-tier3']"
            :aria-label="valuePriceOnSale?.accessibility"
          >
            {{ valuePriceOnSale?.saleValue }} {{ valuePriceOnSale?.rangeValue }}
            <span
              :class="['sale-percent', {'percent-small' : simplificationNonBoldPercentageExp}]"
            >{{ valuePriceOnSale?.salePercent }}</span>
          </span>
        </div>
        <div
          v-if="valuePricing"
          class="value-pricing"
          :aria-label="valuePricing?.accessibility"
        >
          <span class="price-strike-sm">{{ valuePricing?.price }}</span> <span class="price-sm">{{ valuePricing?.value }}</span>
        </div>
      </template>
      <!-- In response, if final price is part of pricing but not part of badges  -->
      <!-- Check if scenario is valid with price simplification -->
      <template v-else-if="hasStandaloneFinalPrice && !isLargeCard && !displaySalePriceOnly">
        <div class="has-standalone-final-price">
          <span :class="finalPrice?.saleType"> {{ finalPrice?.saleValue }}</span>
          <span
            v-if="finalPriceWithRange?.saleValue"
            :class="[finalPriceWithRange?.saleType, 'range-price']"
          >
            {{ finalPriceWithRange?.saleValue }}
          </span>
        </div>
      </template>
      <template v-else-if="hasOnlyTieredPrice2">
        <div>
          <span
            :class="[
              valuePriceOnSale?.saleType != 'regular' && !isStandardSelectItemsOnSale && !isTier2ValuePricing
                ? 'discount'
                : 'price-reg',
              'is-tier2'
            ]"
            :aria-label="valuePriceOnSale?.accessibility"
          >
            {{ valuePriceOnSale?.saleValue }} {{ valuePriceOnSale?.rangeValue }}
            <span
              :class="['sale-percent', {'percent-small' : simplificationNonBoldPercentageExp}]"
            >{{ valuePriceOnSale?.salePercent }}</span>
          </span>
        </div>
        <template v-if="isStandardSelectItemsOnSale && !overrideBCOMPSForCarousels">
          <div class="price-sm">
            Select items on sale
          </div>
        </template>
        <div
          v-if="!overridePriceSimplification && !isStandardSelectItemsOnSale"
          :class="[{'value-pricing-tier2': isTier2ValuePricing} ,'current-prev-value-labels']"
          :aria-label="currentPrevValueLabels?.accessibility"
        >
          <span
            v-if="!isTier2ValuePricing"
            class="price-strike-sm"
          >
            {{ currentPrevValueLabels?.strikePrice }} {{ currentPrevValueLabels?.rangeStrike }}
          </span>
          <span
            v-else
            class="price-sm"
          >
            {{ currentPrevValueLabels?.aValue }}
          </span>
        </div>
      </template>
      <template v-else-if="hasOnlyTieredPrice1">
        <div>
          <span
            :aria-label="valuePriceOnSale?.accessibility"
            class="price-reg is-tier1"
          >
            {{ valuePriceOnSale?.saleValue }} {{ valuePriceOnSale?.rangeValue }}
            <span
              :class="['sale-percent', {'percent-small' : simplificationNonBoldPercentageExp}]"
            >{{ valuePriceOnSale?.salePercent }}</span>
          </span>
        </div>
        <template v-if="isMemberSelectItemsOnSale && !overrideBCOMPSForCarousels">
          <div class="price-sm">
            Select items on sale
          </div>
        </template>
      </template>

      <BadgesSimplification
        :badges-data="badgesData"
        :loyalty-badge="loyaltyBadge"
        :override-price-simplification="overridePriceSimplification"
        :suppress-tool-tip="suppressToolTip"
        :remove-tooltip-on-fgwp-badge-enabled="removeTooltipOnFgwpBadgeEnabled"
        :has-badge-price-data="hasBadgePriceData"
        :is-bcom-final-offer="isBcomFinalOffer"
      />
    </template>
  </div>
</template>

<script>
import Tooltip from '@atomic-ui/tooltip';
import isBcom from '../../../utils/isBcom';
import {
  getTooltipContent,
  plpToolTipClick,
} from '../../../utils/pricingBlockUtil';
import BadgesSimplification from './BadgesSimplification';

export default {
  name: 'PricingBlockSimplification',
  directives: {
    tooltip: Tooltip,
  },
  components: { BadgesSimplification },
  props: {
    pricing: {
      type: Object,
      default: () => { },
    },
    isFinalPrice: {
      type: Boolean,
      default: false,
    },
    isLargeCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPercentOff: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPriceTypeAbovePrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    displaySalePriceOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    overridePriceSimplification: {
      type: Boolean,
      required: false,
      default: false,
    },
    badgesData: {
      type: Array,
      required: false,
      default: () => [],
    },
    simplificationWithV1: {
      type: Boolean,
      default: false,
    },
    simplificationNonBoldPercentageExp: {
      type: Boolean,
      default: false,
    },
    simplificationRemoveSaleExp: {
      type: Boolean,
      default: false,
    },
    loyaltyBadge: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    removeTooltipOnFgwpBadgeEnabled: {
      type: Boolean,
      default: false,
    },
    suppressToolTip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBcom,
      getTooltipContent,
      plpToolTipClick,
    };
  },
  computed: {
    // product with Value price on sale
    valuePriceOnSale() {
      const idxTier = this.pricing?.tieredPrice?.length - 1;
      if (this.pricing?.tieredPrice?.length && this.pricing?.tieredPrice[idxTier]) {
        const priceLabel = !this.isStandardSelectItemsOnSale ? this.getPriceLabel(this.pricing?.tieredPrice[idxTier]?.label) : '';
        const labels = {
          saleValue: `${priceLabel}${this.pricing?.tieredPrice[idxTier]?.values[0].formattedValue}`,
          rangeValue: this.pricing?.tieredPrice[idxTier]?.values[1] ? `- ${this.pricing?.tieredPrice[idxTier]?.values[1]?.formattedValue}` : '',
          salePercent: this.pricing?.tieredPrice[idxTier]?.values[0] ? this.pricing?.tieredPrice[idxTier]?.values[0].formattedPercentOff : '',
          saleType: this.pricing?.tieredPrice[idxTier]?.values[0].type,
          accessibility: '',
        };
        const priceLabelAccessibility = priceLabel || 'Current price ';

        let accessibilityPercent = '';
        if (this.pricing?.tieredPrice[idxTier]?.values[1]?.formattedPercentOff) {
          accessibilityPercent = `From ${this.pricing?.tieredPrice[idxTier]?.values[0].percentOff}% to ${this.pricing?.tieredPrice[idxTier]?.values[1].percentOff}% off`;
        } else if (this.pricing?.tieredPrice[idxTier]?.values[0]?.formattedPercentOff) {
          accessibilityPercent = `${this.pricing?.tieredPrice[idxTier]?.values[0].percentOff}% off`;
        }

        labels.accessibility = labels.rangeValue
          ? `${priceLabelAccessibility} From ${this.pricing?.tieredPrice[idxTier]?.values[0].formattedValue} to ${this.pricing?.tieredPrice[idxTier]?.values[1]?.formattedValue}; ${accessibilityPercent};`
          : `${priceLabelAccessibility} ${this.pricing?.tieredPrice[idxTier]?.values[0].formattedValue}; ${accessibilityPercent};`;

        return labels;
      }
      return null;
    },
    valuePricing() {
      if (this.pricing?.tieredPrice?.length && this.pricing?.tieredPrice[0]?.label?.indexOf('value') > -1) {
        const labels = {
          price: this.pricing?.tieredPrice[1]?.values[0]?.formattedValue || '',
          value: this.pricing?.tieredPrice[0]?.label || '',
          accessibility: '',
        };
        labels.accessibility = `Previous price ${labels.price};`;

        return labels;
      }
      return null;
    },
    hasStandaloneFinalPrice() {
      if (this.isFinalPrice || this.pricing?.finalPrice?.label) {
        return true;
      }
      return false;
    },
    finalPrice() {
      if (this.pricing && this.pricing?.finalPrice?.values[0]?.type !== 'regular') {
        const priceLabel = this.getPriceLabel(this.pricing?.finalPrice?.label);
        return {
          saleValue: `${priceLabel}${this.pricing?.finalPrice?.values[0].formattedValue}`,
          saleType: this.pricing?.finalPrice?.values[0].type,
        };
      }
      return null;
    },
    finalPriceWithRange() {
      if (this.pricing && this.pricing?.finalPrice.values[1]) {
        return {
          saleValue: ` - ${this.pricing?.finalPrice.values[1].formattedValue}`,
          saleType: this.pricing?.finalPrice.values[1].type,
        };
      }
      return null;
    },
    getLabelWithPrice() {
      const lastTieredPrice = this.pricing?.finalPrice || this.pricing?.tieredPrice[this.pricing.tieredPrice.length - 1];
      const values = lastTieredPrice.values.sort((a, b) => (a.value - b.value));
      const priceLabel = this.getPriceLabel(lastTieredPrice.label).trim();
      let price = values[0].formattedValue;
      if (values.length > 1) {
        price += ` - ${values[values.length - 1].formattedValue}`;
      }
      return `${priceLabel} ${price}`.trim();
    },
    hasOnlyTieredPrice3() {
      return this.pricing?.tieredPrice?.length === 3;
    },
    hasOnlyTieredPrice2() {
      return this.pricing?.tieredPrice?.length === 2;
    },
    hasOnlyTieredPrice1() {
      return this.pricing?.tieredPrice?.length === 1;
    },
    hasBadgePriceData() {
      return this.badgesData && this.badgesData[1]?.secondBadgeWithFinalPrice;
    },
    badgeFinalPrice() {
      if (this.hasBadgePriceData) {
        return this.badgesData?.find((badge) => badge.finalPrice?.values[0].formattedValue);
      }
      return null;
    },
    promoCodeIdx() {
      return this.badgesData.findIndex((badge) => 'promoCode' in badge);
    },
    hasPromoCode() {
      return this.promoCodeIdx !== -1;
    },
    badgePriceData() {
      if (this.hasBadgePriceData) {
        const finalPromo = this.promoCodeIdx !== -1 ? this.badgesData[this.promoCodeIdx].promoCode : undefined;

        const labels = {
          salePrice: this.badgeFinalPrice?.finalPrice?.values[0] ? this.badgeFinalPrice?.finalPrice?.values[0]?.formattedValue : '',
          salePercent: this.badgeFinalPrice?.finalPrice?.values[0] ? this.badgeFinalPrice?.finalPrice?.values[0]?.formattedPercentOff : '',
          rangePrice: this.badgeFinalPrice?.finalPrice?.values[1] ? this.badgeFinalPrice?.finalPrice?.values[1]?.formattedValue : '',
          accessibility: '',
          promo: finalPromo,
          description: this.badgesData[0]?.description,
        };

        let accessibilityPercent = '';
        if (labels.rangePrice && this.badgeFinalPrice?.finalPrice?.values[1]?.formattedPercentOff) {
          accessibilityPercent = `From ${this.badgeFinalPrice?.finalPrice?.values[0]?.percentOff}% to ${this.badgeFinalPrice?.finalPrice?.values[1]?.percentOff}% off`;
        } else if (this.badgeFinalPrice?.finalPrice?.values[0]?.formattedPercentOff) {
          accessibilityPercent = `${this.badgeFinalPrice?.finalPrice?.values[0]?.percentOff}% off`;
        }
        const accessibilityText = this.currentPrevValueLabels?.salePrice ? '' : 'Current price ';

        const accessiblilityPromo = this.badgesData[0]?.promoCode ? `with code ${labels.promo}` : labels.promo;
        if (labels.rangePrice) {
          labels.accessibility = `${accessibilityText} From ${labels.salePrice} to ${labels.rangePrice}; ${accessibilityPercent}; ${accessiblilityPromo};`;
        } else {
          labels.accessibility = `${accessibilityText}  ${labels.salePrice}; ${accessibilityPercent}; ${accessiblilityPromo};`;
        }
        return labels;
      }
      return null;
    },
    hasBadgeDiscountOnValue() {
      return this.badgePriceData?.type !== 'regular';
    },
    // Small sale price and previous price values
    currentPrevValueLabels() {
      const labels = {
        salePrice: this.pricing?.tieredPrice[1] ? `Sale ${this.pricing?.tieredPrice[1]?.values[0]?.formattedValue}` : '',
        rangePrice: this.pricing?.tieredPrice[1]?.values[1] ? `- ${this.pricing?.tieredPrice[1]?.values[1]?.formattedValue} ` : '',
        strikePrice: this.pricing?.tieredPrice[0] ? this.pricing?.tieredPrice[0]?.values[0]?.formattedValue : '',
        rangeStrike: this.pricing?.tieredPrice[0]?.values[1] ? `- ${this.pricing?.tieredPrice[0]?.values[1]?.formattedValue}` : '',
        accessibility: '',
      };
      //  fix for tier2 value pricing
      if (this.isTier2ValuePricing) {
        labels.strikePrice = '';
        labels.rangeStrike = '';
        labels.aValue = this.valuePricing?.value;
      }

      const accessibilityText = {
        current: 'Current sale price ',
        previous: 'Previous price ',
      };

      let accessibilitySalePrices = '';
      if (labels.rangePrice) {
        accessibilitySalePrices = `${accessibilityText.current} range ${this.pricing?.tieredPrice[1]?.values[0]?.formattedValue} to ${this.pricing?.tieredPrice[1]?.values[1]?.formattedValue}`;
      } else if (labels.salePrice) {
        accessibilitySalePrices = `${accessibilityText.current} ${this.pricing?.tieredPrice[1]?.values[0]?.formattedValue}`;
      }

      let accessibilityPreviousPrices = '';
      if (labels.rangeStrike) {
        accessibilityPreviousPrices = `${accessibilityText.previous} range from ${this.pricing?.tieredPrice[0]?.values[0]?.formattedValue} to ${this.pricing?.tieredPrice[0]?.values[1]?.formattedValue}`;
      } else if (labels.strikePrice) {
        accessibilityPreviousPrices = `${accessibilityText.previous} ${this.pricing?.tieredPrice[0]?.values[0]?.formattedValue}`;
      }

      labels.accessibility = (!this.hasBadgePriceData && this.hasOnlyTieredPrice2) ? accessibilityPreviousPrices : `${accessibilitySalePrices}; ${accessibilityPreviousPrices};`;

      return labels;
    },
    isLastActId() {
      return this.pricing?.priceTypeId === 8;
    },
    isStandardSelectItemsOnSale() {
      // currently should only apply to is-tier2 prices
      return this.getPriceLabel(this.pricing?.tieredPrice[1]?.label).trim() === 'Now' && this.pricing?.tieredPrice[1]?.values[0]?.type === 'discount' && this.pricing?.tieredPrice[1]?.values[1]?.type === 'regular';
    },
    isMemberSelectItemsOnSale() {
      return isBcom
        ? this.pricing?.priceType?.selectItemsOnSale && (this.pricing?.priceType?.memberProductOnSale || this.pricing?.priceType?.upcOnSale)
        : this.pricing?.priceType?.memberProductOnSale;
    },
    isTier2ValuePricing() {
      return this.hasOnlyTieredPrice2 && this.valuePricing?.value;
    },
    withCodeExperiment() {
      if (isBcom) {
        return 'with ';
      }
      return this.simplificationWithV1 ? 'with ' : 'with code: ';
    },
    isBcomFinalOffer() {
      const idxTier = this.pricing?.tieredPrice?.length - 1;
      if (isBcom && this.pricing?.tieredPrice?.length && this.pricing?.tieredPrice[idxTier]) {
        return this.pricing?.tieredPrice[idxTier]?.values[0].type === 'edv';
      }
      return false;
    },
    overrideBCOMPSForCarousels() {
      return this.isoverridePriceSimplification && !this.isBcom;
    },
  },
  methods: {
    // get label of the price.
    // ex: sale, now, and value
    getPriceLabel(label) {
      if (!this.isTier2ValuePricing) {
        if ((label?.indexOf('Now') > -1) || (label?.indexOf('Only') > -1 && this.badgesData.length === 0)) {
          return 'Now ';
        }
      }
      return '';
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_tooltip-small.scss';
.pricing {
  .discount {
    color: $sale-color;
    font-weight: $font-weight-heavy;
  }

  .show-percent-off {
    display: inline-block;
  }

  &.no-margin-top {
    margin-top: 0;
  }

  .badge-wrapper {
    .tooltip-small {
      vertical-align: bottom;
      background-size: 18px 18px;
      margin-left: 2px;
      height: 18px;
      width: 18px;
    }
  }
}
.price-simplification {
  .sale-percent, .price-strike-sm {
    white-space: nowrap;
    &::before {
      content: " ";
    }
  }
  .percent-small {
    font-weight: 400;
    font-size: rem-calc(12);
    line-height: rem-calc(18);
  }
}
</style>
<style lang="scss" brand="bcom" scoped>
.pricing {
  .price-reg {
    font-weight: $font-weight-heavy;
  }
  .final-price {
    color: $gray-1-color;
  }
}
</style>
